import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserResponse } from '@interfaces/user';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  tap,
  timeout,
} from 'rxjs';
import { environment } from 'src/environments/environment';

type QueryParamsMap = Record<string, any>;
type RefreshErrorResponse = {
  redirectUrl?: string;
};

type TabRefreshMessage =
  | { type: 'REFRESH_STARTED' }
  | { type: 'REFRESH_DONE' }
  | { type: 'REFRESH_FAILED' };

@Injectable({ providedIn: 'root' })
export class AuthSessionFacade {
  private static readonly SHARED_SAT_SESSION_STORAGE_KEY =
    'sat.auth.session.v1';
  private static readonly TOKEN_EXPIRY_COOKIE = 'TOKEN_EXPIRY';
  private static readonly REFRESH_BUFFER_MS =
    environment.authRefreshBufferMs ?? 30000;
  private static readonly CHANNEL_NAME = 'sat-motor.auth.refresh.v1';
  // Bound the blocking time-sync so a slow/hung endpoint can never stall bootstrap.
  private static readonly SERVER_TIME_TIMEOUT_MS = 3000;
  // Plausible epoch-millis window (2020-01-01 .. 2100-01-01) used to reject a
  // malformed server time (e.g. seconds instead of millis -> ~1000x skew).
  private static readonly MIN_PLAUSIBLE_EPOCH_MS = 1_577_836_800_000;
  private static readonly MAX_PLAUSIBLE_EPOCH_MS = 4_102_444_800_000;

  private readonly userSubject$ =
    new BehaviorSubject<Partial<UserResponse> | null>(null);
  private readonly queryParamsSubject$ = new BehaviorSubject<QueryParamsMap>({});
  private readonly rawHttp: HttpClient;
  private readonly tabChannel: BroadcastChannel | null;
  private refreshTimerId: number | null = null;
  private refreshRequest$: Observable<boolean> | null = null;
  private browserHooksRegistered = false;
  private crossTabRefreshInProgress = false;
  private refreshFailureRedirectUrl: string | null = null;
  private clockOffsetMs = 0;

  constructor(
    httpBackend: HttpBackend,
  ) {
    this.rawHttp = new HttpClient(httpBackend);
    this.tabChannel =
      typeof window !== 'undefined' && 'BroadcastChannel' in window
        ? new BroadcastChannel(AuthSessionFacade.CHANNEL_NAME)
        : null;

    this.registerBrowserLifecycleHooks();
    this.registerTabChannel();
  }

  public get userSnapshot(): Partial<UserResponse> | null {
    return this.userSubject$.value;
  }

  public get queryParamsSnapshot(): QueryParamsMap {
    return this.queryParamsSubject$.value;
  }

  public hasSession(): boolean {
    if (this.hasUsableTokenExpiry()) {
      return true;
    }

    this.clearSession();
    return false;
  }

  public clearSession(): void {
    this.cancelRefreshTimer();
    this.userSubject$.next(null);
    this.queryParamsSubject$.next({});
  }

  public setSession(
    user: Partial<UserResponse>,
    queryParams: QueryParamsMap,
  ): void {
    const normalizedParams = this.normalizeQueryParams(queryParams);
    this.userSubject$.next(user);
    this.queryParamsSubject$.next(normalizedParams);
    this.armRefreshTimer();
  }

  public restorePersistedSession(): boolean {
    if (!this.userSubject$.value?.userId) {
      return false;
    }

    if (!this.hasUsableTokenExpiry()) {
      this.clearSession();
      return false;
    }

    this.armRefreshTimer();
    return true;
  }

  public shouldRefreshSoon(
    bufferMs = AuthSessionFacade.REFRESH_BUFFER_MS,
  ): boolean {
    const tokenExpiry = this.readTokenExpiryTimestamp();
    if (!tokenExpiry) {
      return false;
    }

    return this.now() >= tokenExpiry - bufferMs;
  }

  public refreshSession(force = false): Observable<boolean> {
    if (!this.hasSession()) {
      return of(false);
    }

    if (!force && !this.shouldRefreshSoon()) {
      this.armRefreshTimer();
      return of(true);
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    if (this.crossTabRefreshInProgress) {
      return of(true);
    }

    this.tabChannel?.postMessage({ type: 'REFRESH_STARTED' });

    const refreshUrl = `${environment.commonSvcUrl}/auth/refresh`;
    this.refreshFailureRedirectUrl = null;
    this.refreshRequest$ = this.rawHttp
      .post<{ success?: boolean }>(refreshUrl, {}, { withCredentials: true })
      .pipe(
        map((response) => response?.success !== false),
        tap((success) => {
          if (success) {
            this.tabChannel?.postMessage({ type: 'REFRESH_DONE' });
            this.armRefreshTimer();
            return;
          }

          this.tabChannel?.postMessage({ type: 'REFRESH_FAILED' });
          this.cancelRefreshTimer();
        }),
        catchError((error) => {
          this.tabChannel?.postMessage({ type: 'REFRESH_FAILED' });
          this.cancelRefreshTimer();
          this.handleRefreshFailureRedirect(error);
          return of(false);
        }),
        finalize(() => {
          this.refreshRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshRequest$;
  }

  public consumeRefreshFailureRedirectUrl(): string | null {
    const redirectUrl = this.refreshFailureRedirectUrl;
    this.refreshFailureRedirectUrl = null;
    return redirectUrl;
  }

  public syncServerTime(): Observable<void> {
    if (!environment.commonSvcUrl) {
      return of(void 0);
    }

    const serverTimeUrl = `${environment.commonSvcUrl}/time`;
    return this.rawHttp
      .get<{ epochMillis: number }>(serverTimeUrl)
      .pipe(
        timeout(AuthSessionFacade.SERVER_TIME_TIMEOUT_MS),
        tap((response) => {
          const serverEpochMs = response?.epochMillis;
          if (this.isPlausibleEpochMs(serverEpochMs)) {
            this.clockOffsetMs = serverEpochMs - Date.now();
          }
        }),
        map(() => void 0),
        catchError(() => {
          // Never reject: a failed/slow/hung time sync must not block bootstrap
          // (this runs as a blocking APP_INITIALIZER, capped by the timeout
          // above). The offset is intentionally not persisted, so on failure
          // now() falls back to the device clock (clockOffsetMs stays 0 on a
          // fresh load, or the value synced earlier in this session).
          console.warn(
            '[AuthSessionFacade] server time sync failed; falling back to device time',
          );
          return of(void 0);
        }),
      );
  }

  private normalizeQueryParams(queryParams: QueryParamsMap): QueryParamsMap {
    const normalized = { ...queryParams };
    if (normalized['cnoteType'] === 'NW') {
      normalized['cnoteType'] = 'NWOO/RN';
    }
    return normalized;
  }

  public removeSharedSatSession(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    try {
      localStorage.removeItem(AuthSessionFacade.SHARED_SAT_SESSION_STORAGE_KEY);
    } catch {
      // Ignore storage cleanup failures.
    }
  }

  private isPlausibleEpochMs(value: unknown): value is number {
    return (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= AuthSessionFacade.MIN_PLAUSIBLE_EPOCH_MS &&
      value <= AuthSessionFacade.MAX_PLAUSIBLE_EPOCH_MS
    );
  }

  private armRefreshTimer(): void {
    this.cancelRefreshTimer();

    if (
      typeof window === 'undefined' ||
      !this.hasSession() ||
      !this.isDocumentVisible()
    ) {
      return;
    }

    const tokenExpiry = this.readTokenExpiryTimestamp();
    if (!tokenExpiry) {
      return;
    }

    const delay = Math.max(
      tokenExpiry - AuthSessionFacade.REFRESH_BUFFER_MS - this.now(),
      0,
    );

    this.refreshTimerId = window.setTimeout(() => {
      this.refreshSession(true).subscribe();
    }, delay);
  }

  private cancelRefreshTimer(): void {
    if (this.refreshTimerId !== null) {
      window.clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  private registerBrowserLifecycleHooks(): void {
    if (this.browserHooksRegistered || typeof window === 'undefined') {
      return;
    }

    this.browserHooksRegistered = true;

    window.addEventListener('focus', () => this.handleBrowserResume());
    document.addEventListener('visibilitychange', () => {
      if (this.isDocumentVisible()) {
        this.handleBrowserResume();
        return;
      }

      this.cancelRefreshTimer();
    });
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }

      // NGXS storage owns persisted auth metadata in sat-motor.
      // If another tab clears local state, the next guarded navigation or
      // backend auth check will reconcile this tab.
    });
  }

  private handleBrowserResume(): void {
    // The device clock can jump while a tab is backgrounded (laptop sleep/wake,
    // manual clock change), which is exactly when a stale offset would cause a
    // false logout. Re-confirm the server-time offset before re-evaluating the
    // token expiry.
    this.syncServerTime().subscribe(() => this.evaluateSessionAfterResume());
  }

  private evaluateSessionAfterResume(): void {
    if (!this.hasSession()) {
      return;
    }

    if (!this.hasUsableTokenExpiry()) {
      this.clearSession();
      return;
    }

    if (this.shouldRefreshSoon()) {
      this.refreshSession(true).subscribe();
      return;
    }

    this.armRefreshTimer();
  }

  private readTokenExpiryTimestamp(): number | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const cookieValue = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) =>
        entry.startsWith(`${AuthSessionFacade.TOKEN_EXPIRY_COOKIE}=`),
      )
      ?.split('=')[1];

    if (!cookieValue) {
      return null;
    }

    const timestamp = Number(cookieValue);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private hasUsableTokenExpiry(): boolean {
    const tokenExpiry = this.readTokenExpiryTimestamp();
    return tokenExpiry !== null && tokenExpiry > this.now();
  }

  private now(): number {
    return Date.now() + this.clockOffsetMs;
  }

  private isDocumentVisible(): boolean {
    return (
      typeof document === 'undefined' || document.visibilityState !== 'hidden'
    );
  }

  private registerTabChannel(): void {
    if (!this.tabChannel) {
      return;
    }

    this.tabChannel.onmessage = (event: MessageEvent<TabRefreshMessage>) => {
      this.handleTabMessage(event.data);
    };
  }

  private handleTabMessage(msg: TabRefreshMessage): void {
    switch (msg.type) {
      case 'REFRESH_STARTED':
        this.crossTabRefreshInProgress = true;
        this.cancelRefreshTimer();
        break;

      case 'REFRESH_DONE':
        this.crossTabRefreshInProgress = false;
        this.armRefreshTimer();
        break;

      case 'REFRESH_FAILED':
        this.crossTabRefreshInProgress = false;
        if (this.hasUsableTokenExpiry()) {
          this.armRefreshTimer();
        } else {
          this.clearSession();
        }
        break;
    }
  }

  private handleRefreshFailureRedirect(error: unknown): void {
    const redirectUrl = this.extractRefreshFailureRedirectUrl(error);
    if (!redirectUrl) {
      return;
    }

    this.refreshFailureRedirectUrl = redirectUrl;
    this.clearSession();
    this.redirectToUrl(redirectUrl);
  }

  private extractRefreshFailureRedirectUrl(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return null;
    }

    const redirectUrl = (error.error as RefreshErrorResponse | null | undefined)
      ?.redirectUrl;
    return typeof redirectUrl === 'string' && redirectUrl ? redirectUrl : null;
  }

  private redirectToUrl(url: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.location.assign(url);
  }
}
